import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { Entitlements } from '@trello/entitlements';
import { CalendarIcon } from '@trello/nachos/icons/calendar';
import { usePopover } from '@trello/nachos/popover';
import type { QuickCardEditorTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { MultiWorkspacePlanSelectionOverlay } from 'app/src/components/FreeTrial';
import { LazyAddCardToPlannerPopover } from 'app/src/components/Planner/AddCardToPlannerPopover/LazyAddCardToPlannerPopover';
import { PresentationalUpgradePill } from 'app/src/components/Planner/AddCardToPlannerPopover/PresentationalUpgradePill';
import { useUpsell } from 'app/src/components/Planner/useUpsell';
import type { EligibleWorkspaceToUpgrade } from 'app/src/components/SelectWorkspaceToUpgrade';
import { QuickCardEditorButton } from './QuickCardEditorButton';

import * as styles from './AddCardToPlannerButton.module.less';

interface AddCardToPlannerButtonProps {
  onClose: () => void;
}

export const AddCardToPlannerButton: FunctionComponent<
  AddCardToPlannerButtonProps
> = ({ onClose }) => {
  const { popoverProps, toggle, triggerRef } = usePopover();

  const {
    showUpsellModal,
    setShowUpsellModal,
    flagId,
    showPaygatingFlag,
    upsellCohort,
    isUpsellCohortLoading,
  } = useUpsell();

  const eligibilityFilter = (workspace: EligibleWorkspaceToUpgrade) =>
    Entitlements.isFree(workspace.offering);
  const showUpsellFlag = upsellCohort !== 'paidUser';

  const onClick = useCallback(() => {
    if (showUpsellFlag) {
      showPaygatingFlag();
      return;
    }

    toggle();
  }, [showPaygatingFlag, toggle, showUpsellFlag]);

  if (isUpsellCohortLoading) {
    return null;
  }

  return (
    <>
      <QuickCardEditorButton
        icon={<CalendarIcon size="small" />}
        onClick={onClick}
        ref={triggerRef}
        testId={getTestId<QuickCardEditorTestIds>(
          'quick-card-editor-add-card-to-planner',
        )}
        isSelected={popoverProps.isVisible}
        aria-expanded={popoverProps.isVisible}
      >
        <div className={styles.buttonContent}>
          Schedule focus time
          {showUpsellFlag && <PresentationalUpgradePill />}
        </div>
      </QuickCardEditorButton>

      {popoverProps.isVisible && (
        <LazyAddCardToPlannerPopover
          popoverProps={popoverProps}
          onClose={onClose}
        />
      )}
      {showUpsellModal && (
        <MultiWorkspacePlanSelectionOverlay
          onClose={() => setShowUpsellModal(false)}
          currentPlanSelected="premium"
          eligibilityFilter={eligibilityFilter}
          analyticsAttributes={{ upsell: flagId }}
        />
      )}
    </>
  );
};
